/**
 * SOLID Review
 * S — Strong. One concern: what the tab keeps across a reload, as the `ClientTabStorage` port speaks it.
 * O — Good. A new kept record is one more read in `stored` and one more write in `persist`.
 * L — Strong. Implements the port interface exactly.
 * I — Strong. The port's two ops.
 * D — Good. The two storage helper modules, which wrap the browser's storage.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IEntityClientGuest, IEntityClientResume, IEntityClientStored, IPropsClientPersist } from '../../domain/ports/schemas/ClientSession';
import type { IPortClientTabStorage } from '../../application/ports/contracts';
import { readGuest, saveGuest, clearGuestStorage } from './guestIdentityStorage';
import { getResumeSession, saveResumeSession, clearResumeSession } from './sessionTokenStorage';

/**
 * THE TAB'S STORAGE (frontend strict-ports plan, FE2, 2026-10-03): the stored game (`meyer_resume`) and
 * the guest identity, per tab and durable across an OS tab discard. The helpers keep their own legacy
 * migrations and swallow storage errors, as they always have: storage the tab cannot use holds nothing,
 * and a write it cannot make is not a failure of the move that asked for it.
 */
export class ClientTabStorageAdapter implements IPortClientTabStorage {
  /**
   * @param seatSaved called after a seat is stored — the websocket re-introduces itself as the seated player
   *   (the host's `reidentify`, frontend strict-ports FE9: the server learns a seat only at connect time).
   */
  constructor(private readonly seatSaved: () => void) {}

  async stored(_ctx: IContextRequest) {
    const g = readGuest();
    const guest: IEntityClientGuest | null = g.id && g.name ? { playerId: g.id, displayName: g.name } : null;
    const r = getResumeSession();
    const resume: IEntityClientResume | null = r === null ? null : { playerId: r.playerId, roomId: r.roomId, roomCode: r.roomCode };
    const stored: IEntityClientStored = { guest, resume };
    return { success: true as const, value: stored };
  }

  async persist(_ctx: IContextRequest, persist: IPropsClientPersist) {
    if (persist.resume === null) clearResumeSession();
    else if (persist.resume !== undefined) {
      saveResumeSession(persist.resume);
      this.seatSaved();
    }
    // A guest to keep, or — a sign-in or a sign-out (FE3) — none: the stored one is forgotten.
    if (persist.guest === null) clearGuestStorage();
    else if (persist.guest !== undefined) saveGuest(persist.guest.playerId, persist.guest.displayName);
    return { success: true as const, value: undefined };
  }
}
